/**
 * TestFlight Diagnostics Page — temporary instrumentation surface.
 *
 * Route: /diagnostics
 * Also accessible from AccountPage → "TestFlight Diagnostics" row.
 *
 * Pure read-only render of the in-memory state in src/utils/diagnostics.js.
 * Refreshes every second so token-expiry countdown stays current.
 *
 * Safe to remove pre-App-Store: delete this file + route + AccountPage row.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildDiagnosticsSnapshot, clearApiErrors, snapshotToText } from '../utils/diagnostics';
import { ChevronLeft, RefreshCw, Copy, Trash2 } from 'lucide-react';

const Row = ({ label, value, mono = true, danger = false, good = false }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">{label}</span>
    <span
      className={[
        'text-xs text-right break-all',
        mono ? 'font-mono' : '',
        danger ? 'text-red-600 font-semibold' : '',
        good ? 'text-emerald-700' : '',
      ].filter(Boolean).join(' ')}
    >
      {value === null || value === undefined ? '—' : String(value)}
    </span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
    <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">{title}</div>
    {children}
  </div>
);

const DiagnosticsPage = () => {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    // Fully synchronous now — no awaits, no overlapping invocations
    // possible. If anything throws inside buildDiagnosticsSnapshot, we
    // surface it as visible error state instead of hanging.
    try {
      const s = buildDiagnosticsSnapshot();
      setSnap(s);
    } catch (e) {
      setSnap({ error: e?.message || String(e), stack: e?.stack || null });
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 1000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleCopy = async () => {
    if (!snap) return;
    try {
      await navigator.clipboard.writeText(snapshotToText(snap));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select-into-prompt is too ugly; just no-op.
    }
  };

  const handleClearErrors = () => {
    clearApiErrors();
    refresh();
  };

  if (!snap) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-700 mb-4"
          data-testid="diagnostics-back-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-sm text-gray-500">Loading diagnostics…</div>
      </div>
    );
  }

  // If the synchronous snapshot itself threw, render a hard error panel
  // so we never hang on a blank "Loading…" screen — Kent's exact symptom.
  if (snap.error) {
    return (
      <div className="min-h-screen bg-red-50 p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-700 mb-4"
          data-testid="diagnostics-back-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-lg font-bold text-red-700 mb-2">Diagnostics failed</h1>
        <pre className="text-xs text-red-900 whitespace-pre-wrap break-all bg-white p-3 rounded border border-red-200">
{snap.error}
{snap.stack ? `\n\n${snap.stack}` : ''}
        </pre>
        <button
          onClick={refresh}
          className="mt-3 px-3 py-1.5 text-xs bg-red-600 text-white rounded"
          data-testid="diagnostics-retry-btn"
        >
          Retry
        </button>
      </div>
    );
  }

  const t = snap.tokens || {};
  const r = snap.resume || {};
  const rv = snap.revalidation || {};
  const a = snap.auth || {};
  const u = snap.user || {};

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-24" data-testid="diagnostics-page">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-700"
          data-testid="diagnostics-back-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="p-2 rounded-md hover:bg-gray-100"
            title="Refresh"
            data-testid="diagnostics-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={handleCopy}
            className="p-2 rounded-md hover:bg-gray-100"
            title="Copy snapshot JSON"
            data-testid="diagnostics-copy-btn"
          >
            <Copy className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      <h1 className="text-xl font-bold text-gray-900">TestFlight Diagnostics</h1>
      <p className="text-xs text-gray-500 mb-3">
        Temporary panel — branch <span className="font-mono">{snap.branch}</span>. Updates every 1s.
      </p>
      {copied && <p className="text-xs text-emerald-600 mb-2">Snapshot copied to clipboard.</p>}

      <Section title="Build">
        <Row label="Branch" value={snap.branch} />
        <Row label="Built At" value={snap.builtAt} />
        <Row label="Node Env" value={snap.nodeEnv} />
        <Row label="Snapshot At" value={snap.snapshotTakenAt} />
      </Section>

      <Section title="User">
        <Row label="Email" value={u.email} />
        <Row label="User Id" value={u.id} />
        <Row label="Roles" value={(u.roles || []).join(', ') || '—'} />
        <Row label="Is Admin" value={u.isAdmin ? 'yes' : 'no'} />
        <Row label="Active Role" value={u.activeRole} />
        <Row label="Impersonation" value={u.impersonationActive ? 'ACTIVE' : 'no'} danger={u.impersonationActive} />
      </Section>

      <Section title="Tokens">
        <Row
          label="Access Token Present"
          value={t.accessTokenPresent ? 'yes' : 'NO'}
          danger={!t.accessTokenPresent}
          good={!!t.accessTokenPresent}
        />
        <Row label="Looks Like JWT" value={t.accessTokenLooksValid ? 'yes' : 'no'} />
        <Row label="Expires At" value={t.accessTokenExpiresAt} />
        <Row
          label="Expires In (s)"
          value={t.accessTokenExpiresInSeconds}
          danger={typeof t.accessTokenExpiresInSeconds === 'number' && t.accessTokenExpiresInSeconds <= 0}
          good={typeof t.accessTokenExpiresInSeconds === 'number' && t.accessTokenExpiresInSeconds > 60}
        />
        <Row
          label="Currently Expired"
          value={t.accessTokenExpired === null ? '—' : (t.accessTokenExpired ? 'YES' : 'no')}
          danger={t.accessTokenExpired === true}
          good={t.accessTokenExpired === false}
        />
        <Row label="Native Refresh Token" value={String(t.nativeRefreshTokenPresent)} />
      </Section>

      <Section title="Resume Engine">
        <Row label="Last Backgrounded" value={r.lastBackgroundedAt} />
        <Row label="Last Resume" value={r.lastResumeAt} />
        <Row label="Last Resume Idle (ms)" value={r.lastResumeIdleMs} />
        <Row label="Last Resume Reason" value={r.lastResumeReason} />
        <Row label="resumeEpoch" value={r.lastResumeEpoch} />
        <Row label="tapEpoch" value={r.lastTapEpoch} />
        <Row
          label="Raw Foreground Events"
          value={r.rawForegroundEvents}
          danger={(r.rawForegroundEvents || 0) === 0 && !!r.lastBackgroundedAt}
        />
        <Row label="Last Raw Foreground At" value={r.lastRawForegroundAt} />
        <Row label="Last Raw Foreground Source" value={r.lastRawForegroundSource} />
      </Section>

      {snap.refreshToken && (
        <Section title="Refresh Token Activity">
          <Row
            label="Session Durability"
            value={snap.refreshToken.sessionDurability}
            good={snap.refreshToken.sessionDurability === 'durable'}
            danger={snap.refreshToken.sessionDurability === 'volatile'}
          />
          <Row label="Durability Set At" value={snap.refreshToken.sessionDurabilityLastSetAt} />
          <Row label="Durability Reason" value={snap.refreshToken.sessionDurabilityLastReason} />
          <Row label="Write Count" value={snap.refreshToken.writeCount} />
          <Row label="Last Write At" value={snap.refreshToken.lastWriteAt} />
          <Row label="Last Write Source" value={snap.refreshToken.lastWriteSource} />
          <Row
            label="Last Write Outcome"
            value={snap.refreshToken.lastWriteOutcome}
            good={snap.refreshToken.lastWriteOutcome === 'success'}
            danger={!!snap.refreshToken.lastWriteOutcome && snap.refreshToken.lastWriteOutcome !== 'success'}
          />
          <Row label="Last Write Error" value={snap.refreshToken.lastWriteError} />
          <Row label="Read Count" value={snap.refreshToken.readCount} />
          <Row label="Last Read At" value={snap.refreshToken.lastReadAt} />
          <Row label="Last Read Source" value={snap.refreshToken.lastReadSource} />
          <Row
            label="Last Read Result"
            value={snap.refreshToken.lastReadResult}
            good={['cache-hit', 'cold-read-success'].includes(snap.refreshToken.lastReadResult)}
            danger={['missing', 'null', 'empty-string', 'timeout', 'error'].includes(snap.refreshToken.lastReadResult)}
          />
          <Row label="Clear Count" value={snap.refreshToken.clearCount} danger={(snap.refreshToken.clearCount || 0) > 0} />
          <Row label="Last Cleared At" value={snap.refreshToken.lastClearedAt} />
          <Row label="Last Clear Reason" value={snap.refreshToken.lastClearReason} />
          <Row
            label="Cache Present"
            value={snap.refreshToken.cachePresent}
            good={snap.refreshToken.cachePresent === 'true'}
            danger={snap.refreshToken.cachePresent === 'false'}
          />
          <Row
            label="Persistence Present"
            value={snap.refreshToken.persistencePresent}
            good={snap.refreshToken.persistencePresent === 'true'}
            danger={['false', 'get-timeout', 'module-timeout', 'error'].includes(snap.refreshToken.persistencePresent)}
          />
        </Section>
      )}

      <Section title="Session Revalidation">
        <Row label="Last Attempt At" value={rv.lastRevalidationAt} />
        <Row
          label="Last Outcome"
          value={rv.lastRevalidationOutcome}
          danger={rv.lastRevalidationOutcome === 'auth-fail'}
          good={rv.lastRevalidationOutcome === 'success'}
        />
        <Row label="Last Error" value={rv.lastRevalidationErrorMessage} />
      </Section>

      <Section title="Auth State Flags">
        <Row
          label="Cached User Fallback"
          value={a.cachedUserFallbackActive ? 'ACTIVE' : 'inactive'}
          danger={a.cachedUserFallbackActive}
        />
        <Row label="Fallback Reason" value={a.cachedUserFallbackReason} />
        <Row
          label="Axios Is Refreshing"
          value={a.isRefreshingFlag ? 'yes' : 'no'}
          danger={a.isRefreshingFlag}
        />
        {a.gate && (
          <>
            <Row
              label="Session Expired (Gate)"
              value={a.gate.sessionExpiredFlag ? 'LOCKED OUT' : 'no'}
              danger={a.gate.sessionExpiredFlag}
              good={!a.gate.sessionExpiredFlag}
            />
            <Row label="Gate Last Reason" value={a.gate.sessionExpiredLastReason} />
            <Row
              label="Refresh In Flight"
              value={a.gate.refreshInFlight ? 'YES' : 'no'}
              danger={a.gate.refreshInFlight}
            />
            <Row label="Last Refresh Error" value={a.gate.lastRefreshError} />
          </>
        )}
      </Section>

      <Section title={`Recent API Errors (${(snap.apiErrors || []).length})`}>
        <div className="flex justify-end mb-2">
          <button
            onClick={handleClearErrors}
            className="text-xs flex items-center gap-1 text-gray-500 hover:text-red-600"
            data-testid="diagnostics-clear-errors-btn"
          >
            <Trash2 className="h-3 w-3" /> Clear
          </button>
        </div>
        {(snap.apiErrors || []).length === 0 ? (
          <p className="text-xs text-gray-400">No API errors recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {(snap.apiErrors || []).map((e, i) => (
              <div key={i} className="border border-gray-100 rounded p-2 bg-gray-50">
                <div className="flex justify-between text-xs font-mono">
                  <span className={e.status >= 500 ? 'text-red-600' : (e.status === 401 || e.status === 403) ? 'text-amber-700' : 'text-gray-700'}>
                    {e.method} {e.url}
                  </span>
                  <span className="text-gray-500">{e.status ?? e.code ?? 'ERR'}</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">{e.at}</div>
                {e.message && <div className="text-[11px] text-gray-700 mt-1">{e.message}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <p className="text-[10px] text-gray-400 text-center mt-4">
        Diagnostics scaffold — remove before App Store submission. See utils/diagnostics.js header for cleanup steps.
      </p>
    </div>
  );
};

export default DiagnosticsPage;
